import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';
import { ArrowUpDown } from 'lucide-react';
import PageHero from '../components/PageHero';
import { trackMetaEvent } from '../utils/metaPixel';

const Products = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('sortOrder'); // sortOrder, priceAsc, priceDesc, nameAsc

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const q = query(
                    collection(db, 'products'),
                    where('status', '==', 'published'),
                    orderBy('sortOrder', 'asc')
                );
                const snapshot = await getDocs(q);
                const productsData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setProducts(productsData);

                // Track ViewContent for product catalog
                if (productsData.length > 0) {
                    trackMetaEvent('ViewContent', {
                        content_ids: productsData.map(p => p.id),
                        content_type: 'product_group',
                        content_category: 'All Products'
                    });
                }
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    // Sort products based on selected option
    const sortedProducts = [...products].sort((a, b) => {
        switch (sortBy) {
            case 'priceAsc':
                return (a.price || 0) - (b.price || 0);
            case 'priceDesc':
                return (b.price || 0) - (a.price || 0);
            case 'nameAsc':
                return (a.name || '').localeCompare(b.name || '');
            case 'sortOrder':
            default:
                return (a.sortOrder || 0) - (b.sortOrder || 0);
        }
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#E85D44]"></div>
            </div>
        );
    }

    return (
        <main className="bg-[#F5F0EB] min-h-screen">
            <Helmet>
                <title>Our Products | Merch Masters LK</title>
                <meta name="description" content="Browse our collection of personalized 3D printed products. Transform your memories into unique, handcrafted treasures." />
            </Helmet>

            <PageHero
                title="All Products"
                subtitle="Discover our unique collection of personalized 3D printed products"
            />

            {/* Products Section */}
            <section className="py-16 px-6">
                <div className="max-w-screen-xl mx-auto">
                    {/* Sorting Controls */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
                        <p className="text-gray-600">
                            Showing <span className="font-bold text-gray-900">{sortedProducts.length}</span> {sortedProducts.length === 1 ? 'product' : 'products'}
                        </p>

                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <ArrowUpDown className="w-4 h-4 text-[#E85D44]" />
                                Sort by:
                            </label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:border-[#E85D44] transition-colors cursor-pointer hover:border-gray-300"
                            >
                                <option value="sortOrder">Default Order</option>
                                <option value="priceAsc">Price: Low to High</option>
                                <option value="priceDesc">Price: High to Low</option>
                                <option value="nameAsc">Name: A to Z</option>
                            </select>
                        </div>
                    </div>

                    {/* Products Grid */}
                    {sortedProducts.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-2xl text-gray-600 mb-4">No products available yet</p>
                            <p className="text-gray-500">Check back soon for amazing personalized products!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                            {sortedProducts.map((product) => (
                                <Link
                                    key={product.id}
                                    to={`/products/${product.slug}`}
                                    className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2"
                                >
                                    {/* Product Image */}
                                    <div className="aspect-[4/5] overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 relative">
                                        {product.primaryImage ? (
                                            <img
                                                src={product.primaryImage.url}
                                                alt={product.name}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                                                No Image
                                            </div>
                                        )}
                                        {product.badge && (
                                            <span className="absolute top-4 left-4 bg-[#E85D44] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                                                {product.badge}
                                            </span>
                                        )}
                                        {/* Hover Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                                            <span className="text-white font-semibold text-sm uppercase tracking-wider">
                                                View Details →
                                            </span>
                                        </div>
                                    </div>

                                    {/* Product Info */}
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#E85D44] transition-colors line-clamp-1">
                                            {product.name}
                                        </h3>
                                        <p className="text-gray-600 text-sm mb-4 line-clamp-2 h-10">
                                            {product.shortDescription}
                                        </p>
                                        <div className="flex items-center justify-between">
                                            <span className="text-2xl font-bold text-gray-900">
                                                {(() => {
                                                    const enabledSizes = (product.sizes || []).filter(s => s.enabled);
                                                    if (enabledSizes.length > 0) {
                                                        const prices = enabledSizes.map(s => s.priceDelta || product.price);
                                                        const minPrice = Math.min(...prices);
                                                        const maxPrice = Math.max(...prices);

                                                        if (minPrice !== maxPrice) {
                                                            return `${product.priceCurrency || 'Rs.'} ${minPrice.toLocaleString()} - ${maxPrice.toLocaleString()}`;
                                                        }
                                                        return `${product.priceCurrency || 'Rs.'} ${minPrice.toLocaleString()}`;
                                                    }
                                                    return `${product.priceCurrency || 'Rs.'} ${product.price?.toLocaleString()}`;
                                                })()}
                                            </span>
                                            <div className="w-10 h-10 rounded-full bg-[#E85D44]/10 flex items-center justify-center group-hover:bg-[#E85D44] transition-colors">
                                                <svg className="w-5 h-5 text-[#E85D44] group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-6 bg-gradient-to-br from-[#E85D44] to-[#ff5c4d] text-white text-center">
                <div className="max-w-screen-xl mx-auto">
                    <h2 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
                        Can't Find What You're Looking For?
                    </h2>
                    <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto">
                        We can create custom products just for you. Get in touch with our team to discuss your unique requirements.
                    </p>
                    <a
                        href="https://wa.me/94771234567?text=Hi! I'd like to inquire about custom products."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-3 px-8 py-4 bg-white text-[#E85D44] hover:bg-gray-100 font-semibold text-sm uppercase tracking-[0.2em] rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        Contact Us on WhatsApp
                    </a>
                </div>
            </section>
        </main>
    );
};

export default Products;
